import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { environment } from '../../environments/environment';
import { SessionExpiredDialog, SessionExpiredError, needsReLogin } from '@iqd/shared-app';
import { HostAuthProvider } from './host-auth-context';

const ROLES_CLAIM = environment.auth0.claimsNamespace + 'roles';
// Named `company`, but it holds the distributor's uuid rather than its id.
const COMPANY_CLAIM = environment.auth0.claimsNamespace + 'company';
// Holds the widget list and the news feed as well as the demo flag.
const DASHBOARD_CLAIM = environment.auth0.claimsNamespace + 'dashboard';
// The connections behind this account, so a screen can tell what is already
// linked. Absent until a tenant's post-login Action sets it.
const IDENTITIES_CLAIM = environment.auth0.claimsNamespace + 'identities';

/** The widths of the greyed-out nav items, roughly what the real labels take. */
const NAV_PLACEHOLDERS = [58, 74, 66, 84, 52, 70];

/**
 * The frame, drawn while Auth0 works out who this is.
 *
 * `isLoading` covers a silent-auth round trip through an iframe, which is not
 * quick, and returning `null` for it left the window blank the whole time —
 * the emptiest possible answer at the moment someone is most likely to wonder
 * whether anything is happening.
 *
 * The real menu bar cannot be drawn yet: it reads roles and the Apollo cache,
 * and neither exists above the providers this sits outside of. So this is its
 * box at the same height, with the items greyed, and nothing shifts when the
 * real one takes over. The class lists are `AppShell`'s and `MenuBar`'s on
 * purpose — if either changes shape, this has to follow.
 */
function AppShellSkeleton() {
  const [searchParams] = useSearchParams();
  // A window dragged onto a second monitor has no chrome, so its placeholder
  // should not grow any either.
  const chrome = searchParams.get('popout') !== '1';

  return (
    <div className="flex min-h-screen flex-col bg-white" role="status" aria-busy="true">
      <span className="sr-only">Signing in</span>

      {chrome && (
        <nav className="border-b border-hilevel-border bg-hilevel-offwhite">
          {/* The same three groups as the real bar, with the same wrapping
              rules, so it is the same height at any width. Guessing at a
              single row of items instead was right on a wide screen and half
              the height of the real thing on a narrow one, where the bar wraps
              the right-hand group onto a second line. */}
          <div className="flex flex-wrap items-center gap-1 px-4 py-2">
            {/* Below `lg` the bar collapses to this one button. */}
            <div className="h-[34px] w-[38px] animate-pulse rounded border border-hilevel-border bg-hilevel-light lg:hidden" />

            <div className="hidden flex-wrap items-center gap-1 lg:flex">
              {NAV_PLACEHOLDERS.map((width, index) => (
                <div
                  key={index}
                  style={{ width }}
                  // A nav item is 32px: `text-sm` gives it a 20px line box and
                  // `py-1.5` another 12px.
                  className="h-8 animate-pulse rounded bg-hilevel-light"
                />
              ))}
            </div>

            {/* Sign-in and support, which the real bar pushes to the right. The
                two here are 34px rather than 32 because those carry a border. */}
            <div className="ml-auto flex items-center gap-2">
              <div className="h-[34px] w-[72px] animate-pulse rounded bg-hilevel-light" />
              <div className="h-[34px] w-[68px] animate-pulse rounded bg-hilevel-light" />
            </div>
          </div>
        </nav>
      )}

      <main className="flex-1 p-4">
        <div className="h-40 animate-pulse rounded-lg bg-hilevel-light" />
      </main>
    </div>
  );
}

export function Auth0Bridge({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const [sessionExpired, setSessionExpired] = useState(false);
  // Once someone has said "not now", stop asking. Every query on the screen
  // fails the same way and there can be a dozen of them, so reopening on each
  // would leave them unable to read the page they are being signed out of.
  const dismissed = useRef(false);

  /**
   * The token every screen asks for, with a signed-out session answered here
   * rather than sixty screens down.
   *
   * The rejection still happens — a query that cannot authenticate has not
   * succeeded, and pretending otherwise would leave screens waiting on data
   * that is never coming — but it carries a sentence a person can act on, and
   * the dialog beside it is what actually offers the way back.
   */
  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      if (!needsReLogin(error)) throw error;
      if (!dismissed.current) setSessionExpired(true);
      throw new SessionExpiredError();
    }
  }, [getAccessTokenSilently]);

  if (isLoading) return <AppShellSkeleton />;

  return (
    <HostAuthProvider
      auth={{
        user: user
          ? {
              sub: user.sub,
              name: user.name,
              email: user.email,
              picture: user.picture,
              companyUuid: user[COMPANY_CLAIM] as string | undefined,
              demoMode: (user[DASHBOARD_CLAIM] as { demo_mode?: boolean })?.demo_mode ?? false,
              linkedConnections: (user[IDENTITIES_CLAIM] as string[]) ?? [],
              roles: (user[ROLES_CLAIM] as string[]) ?? [],
            }
          : null,
        isAuthenticated,
        getAccessToken,
      }}
    >
      {children}
      <SessionExpiredDialog
        open={sessionExpired}
        onClose={() => {
          dismissed.current = true;
          setSessionExpired(false);
        }}
        // Where to come back to. `loginWithRedirect` leaves the document, so
        // this is the only record of which screen the session died on.
        onSignIn={() =>
          loginWithRedirect({
            appState: { returnTo: `${window.location.pathname}${window.location.search}` },
          })
        }
      />
    </HostAuthProvider>
  );
}
